<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div class="invoice-details order-details">
      <div class="row">
				<div class="col-md-12">
          <h3><template v-if="invoice && parseInt(invoice.isrefund)">{{ _('Refund') }}</template><template v-else>{{ _('Transaction') }}</template> {{ invoice.id }}</h3>
        </div>
      </div>
      <lock-info v-if="isAnonymizedRecord" :locked="true" :lockStatus="anonymizedLockStatus" :readOnly="true" />
      <lock-info v-else-if="locked" :locked="locked" :lockStatus="lockStatus" v-on:forceLock="getComponentLock(true)" />
      <editor-tabs id="tabNavi" :tabs="tabList" :selected="selectedTab" :id-prefix="tabIdPrefix" v-on:select="tab" />
      <div class="tab-content">
        <transition-group name="fade">
          <div v-bind="tabPanel('general')" class="tab-pane active" id="general" v-if="isTabActive('general')" key="general">
            <div class="row">
              <div class="col-md-12">
                <div class="invoice-items" v-if="allItems && allItems.length">
                  <div class="table-responsive">
                    <table class="table table-striped" id="productTable">
                      <thead>
                        <tr>
                          <th>{{ _('ID') }}</th>
                          <th>{{ _('Product name') }}</th>
                          <th>{{ _('Amount') }}</th>
                          <th class="hidden-print">{{ _('Unit price (inc. vat)') }}</th>
                          <th class="hidden-print">{{ _('Total (inc. vat)') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <template v-for="item in topLevelItems" :key="item.id">
                          <component :is="getItemComponent(item)" :item="item" v-on:reservationClicked="displayReservation" v-on:resourceClicked="displayResource" v-on:valuecardClicked="displayValuecard" />
                          <component :is="getItemComponent(option)" v-for="option in getItemOptions(item)" :is-option="true" :key="option.id" :item="option" v-on:reservationClicked="displayReservation" v-on:resourceClicked="displayResource" v-on:valuecardClicked="displayValuecard" />
                        </template>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <div class="row" v-if="isResendBarVisible">
              <div class="col-md-12">
                <button v-if="hasActiveValueCards" class="btn btn-primary" type="button" v-on:click="resendValueCardEmailDialog"><i class="fa fa-fw fa-retweet"></i> {{ _('Resend value card email') }}</button>
                <button v-else-if="hasValueCards" class="btn btn-primary" type="button" v-on:click="activateValueCardsDialog"><i class="fa fa-fw fa-check-to-slot"></i> {{ _('Activate value cards') }}</button>
                <button v-if="hasTickets" class="btn btn-primary" type="button" v-on:click="resendTicketEmailDialog"><i class="fa fa-fw fa-retweet"></i> {{ _('Resend ticket email') }}</button>
              </div>
            </div>
            <div class="row">
              <div class="col-sm-6 rowspacing">
                <h3>{{ _('Summary') }}</h3>
                <table class="table">
                  <tbody>
                    <tr v-if="parseInt(invoice.delivery)">
                      <td width="50%">{{ _('Delivery method') }}</td>
                      <td>{{ deliveryName }}<span class="hidden-print">, {{ $filters.currency(invoice.totalWithDelivery - invoice.total, settings.currency) }}</span></td>
                    </tr>
                    <tr class="hidden-print">
                      <td width="50%">{{ _('Total (vat 0%)') }}</td>
                      <td>{{ $filters.currency(invoice.totalWithDeliveryVatFree, settings.currency) }}</td>
                    </tr>
                    <tr class="hidden-print">
                      <td width="50%">{{ _('Total vat') }}</td>
                      <td>{{ $filters.currency(invoice.totalWithDeliveryVat, settings.currency) }}</td>
                    </tr>
                    <tr class="hidden-print">
                      <td width="50%">{{ _('Final price') }}</td>
                      <td>{{ $filters.currency(invoice.totalWithDelivery, settings.currency) }}</td>
                    </tr>
                    <tr v-if="invoice.payment">
                      <td width="50%">{{ _('Payment method') }}</td>
                      <td>{{ paymentName }}</td>
                    </tr>
                    <tr v-if="bamboraId">
                      <td width="50%">{{ _('Visma Pay order number') }}</td>
                      <td>{{ bamboraId }}</td>
                    </tr>
                    <tr v-if="vivaId">
                      <td width="50%">{{ _('Viva order number') }}</td>
                      <td>{{ vivaId }}</td>
                    </tr>
                    <tr v-if="parseInt(invoice.groupdeal)">
                      <td width="50%">{{ _('Group deal') }}</td>
                      <td>{{ _('Yes') }}</td>
                    </tr>
                  </tbody>
                </table>
                <h3>{{ _('Transaction details') }}</h3>
                <table class="table">
                  <tbody>
                    <tr v-if="invoice.version">
                      <td width="50%">{{ _('Language') }}</td>
                      <td>{{ getVersionName(invoice.version) }}</td>
                    </tr>
                    <tr v-if="!parseInt(invoice.isrefund)">
                      <td width="50%">{{ _('Order date') }}</td>
                      <td>{{ $filters.timestampToDate(invoice.orderdate) }} {{ _('at') }} {{ $filters.timestampToTime(invoice.orderdate) }}</td>
                    </tr>
                    <tr>
                      <td width="50%">{{ _('Date') }}</td>
                      <td>{{ $filters.timestampToDate(invoice.invoicedate) }} {{ _('at') }} {{ $filters.timestampToTime(invoice.invoicedate) }}</td>
                    </tr>
                    <tr v-if="isInvoiceOrTransfer">
                      <td width="50%">{{ _('Due date') }}</td>
                      <td>{{ $filters.timestampToDate(invoice.duedate) }}</td>
                    </tr>
                    <tr v-if="isInvoiceOrTransfer">
                      <td width="50%">{{ _('Payment term') }}</td>
                      <td v-if="invoice.duedate === 'HETI'">{{ _('IMMEDIATELY') }}</td>
                      <td v-else-if="parseInt(invoice.pay_option1_duedays)">{{ invoice.pay_option1_duedays }} {{ _('days') }}</td>
                      <td v-else>{{ _('As agreed') }}</td>
                    </tr>
                    <tr v-if="invoice.accounting_invoice_number">
                      <td width="50%">{{ _('Invoice number') }}</td>
                      <td>{{ invoice.accounting_invoice_number }}</td>
                    </tr>
                    <tr v-if="isInvoiceReferenceVisible">
                      <td width="50%">{{ _('Reference number') }}</td>
                      <td>{{ invoice.accounting_reference || invoice.refnumber }}</td>
                    </tr>
                    <tr v-if="invoice.transactionid">
                      <td width="50%">{{ _('External reference') }}</td>
                      <td>{{ invoice.transactionid }}</td>
                    </tr>
                    <tr v-if="parseInt(invoice.transactionid) === -1 && invoice.error">
                      <td colspan="2" class="text-danger"><i>{{ invoice.error }}</i></td>
                    </tr>
                    <tr v-if="invoice.reference">
                      <td width="50%">{{ _('Reference') }}</td>
                      <td>{{ invoice.reference }}</td>
                    </tr>
                    <tr v-if="invoice.yourreference">
                      <td width="50%">{{ _('Your reference') }}</td>
                      <td>{{ invoice.yourreference }}</td>
                    </tr>
                    <tr v-if="invoice.mark">
                      <td width="50%">{{ _('Mark') }}</td>
                      <td>{{ invoice.mark }}</td>
                    </tr>
                    <tr v-if="invoice.src">
                      <td width="50%">{{ _('Order source page') }}</td>
                      <td style="word-break: break-all;"><a v-if="/^https?:\/\//i.test(invoice.src)" target="_blank" rel="noopener noreferrer" :href="invoice.src">{{ invoice.src }}</a><template v-else>{{ invoice.src }}</template></td>
                    </tr>
                    <tr v-if="invoice.origin">
                      <td width="50%">{{ _('Order source channel') }}</td>
                      <td style="word-break: break-all">{{ invoice.origin.replace(/channel/, '') }}<span v-if="invoice.pos_name"> / {{ invoice.pos_name }}</span></td>
                    </tr>
                    <tr v-if="invoice.refunds_id">
                      <td width="50%">{{ _('Refunded invoice number') }}</td>
                      <td>{{ invoice.refunds_id }}</td>
                    </tr>
                    <tr v-if="invoice.refund_id">
                      <td width="50%">{{ _('Refunded by invoice number') }}</td>
                      <td>{{ invoice.refund_id }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="col-sm-6">
                <h3>{{ _('Customer details') }}</h3>
                <p><strong>{{ invoice.name }}</strong></p>
                <span v-if="invoice.ident">{{ invoice.ident }}<br /></span>
                <p>
                  <span v-if="invoice.address">{{ invoice.address }}<br/></span>
                  <span v-if="invoice.zip || invoice.city">{{ invoice.zip }} {{ invoice.city }}<br /></span>
                  <span v-if="invoice.country">{{ getCountryName(invoice.country) }}<br /></span>
                  <span v-if="invoice.phone">{{ _('tel.') }} {{ invoice.phone }}<br/></span>
                  <span v-if="invoice.company">{{ invoice.company }}<br/></span>
                  <span v-if="invoice.evoice">{{ _('E-Invoice Address') }}: {{ invoice.evoice }}<br /></span>
                  <span v-if="invoice.evoiceint">{{ _('E-Invoice Intermediary') }}: {{ invoice.evoiceint }}<br/></span>
                  <span v-if="invoice.email">{{ _('Email') }}: <a :href="'mailto:' + invoice.email">{{ invoice.email }}</a></span>
                </p>
                <span v-if="invoice.more">
                  <h3>{{ _('Additional info') }}</h3>
                  <p style="white-space: pre-line">{{ invoice.more }}</p>
                </span>
                <div v-if="verifiedCustomer" class="well">
                  <h4 style="margin-top: 0"><i class="fa fa-fw fa-check-square-o"></i> {{ _('Verified customer') }}</h4>
                  <span>{{ verifiedCustomer.name }}<br /></span>
                  <span>{{ verifiedCustomer.dateOfBirth }}<br /></span>
                  <span>{{ verifiedCustomer.nin.issuingCountry }}<br /></span>
                  <span style="font-size: 10px">{{ _('Verified at') }}: {{ $filters.timestampToDate(verifiedCustomer.ts) }} {{ $filters.timestampToTime(verifiedCustomer.ts) }}</span>
                  <span style="font-size: 10px">/ ID: {{ verifiedCustomer.id }}</span>
                </div>
                <a class="btn btn-primary hidden-print" v-on:click="displayCustomer" v-if="invoice.customer_id"><i class="fa fa-fw fa-male"></i> {{ _('Customer') }}</a>
                <div class="checkbox" v-if="!isPaid">
                  <label>
                    <input type="checkbox" v-model="notificationsWithoutPayment">
                    {{ _('Send notifications without payment') }}
                  </label>
                </div>
                <h3>{{ _('Billing info') }}</h3>
                <span v-if="hasBillingCustomer">
                  <p v-if="invoice.billing_company"><strong>{{ invoice.billing_company }}</strong></p>
                  <span v-if="invoice.billing_department">{{ invoice.billing_department }}<br /></span>
                  <span v-if="invoice.billing_ident">{{ invoice.billing_ident }}<br /></span>
                  <p>
                    <span v-if="invoice.billing_address">{{ invoice.billing_address }}<br /></span>
                    <span v-if="invoice.billing_zip || invoice.billing_city">{{ invoice.billing_zip }} {{ invoice.billing_city }}<br /></span>
                    <span v-if="invoice.billing_country">{{ getCountryName(invoice.billing_country) }}<br /></span>
                    <span v-if="invoice.billing_phone">{{ _('tel.') }} {{ invoice.billing_phone }}<br /></span>
                    <span v-if="invoice.billing_name">{{ invoice.billing_name }}<br /></span>
                    <span v-if="invoice.billing_evoice">{{ _('E-Invoice Address') }}: {{ invoice.billing_evoice }}<br /></span>
                    <span v-if="invoice.billing_evoiceint">{{ _('E-Invoice Intermediary') }}: {{ invoice.billing_evoiceint }}<br/></span>
                    <span v-if="invoice.billing_billingemail">{{ _('Billing Email') }}: <a :href="'mailto:' + invoice.billing_billingemail">{{ invoice.billing_billingemail }}</a><br /></span>
                    <span v-if="invoice.billing_email">{{ _('Email') }}: <a :href="'mailto:' + invoice.billing_email">{{ invoice.billing_email }}</a></span>
                  </p>
                  <a class="btn btn-primary btn-margin-bottom hidden-print" v-on:click="displayBillingCustomer" v-if="invoice.billingcustomer_id"><i class="fa fa-fw fa-building-o"></i> {{ _('Billing Customer') }}</a>
                </span>
                <p v-else><strong>{{ _('No billing customer set, using customer details.') }}</strong></p>
              </div>
            </div>
            <h3 v-if="isRefunded">{{ _('The order has been refunded!') }}</h3>
            <hr v-if="invoice.identifier"/>
            <div class="button-row" v-if="invoice.identifier">
              <a class="btn btn-primary btn-margin-bottom" v-if="isPrintReceiptVisible" v-on:click="printReceipt"><i class="fa fa-file-pdf fa-fw"></i> {{ _('PDF receipt') }}</a>
              <a class="btn btn-primary btn-margin-bottom" v-if="isPrintInvoiceVisible" v-on:click="printInvoice"><i class="fa fa-file-pdf fa-fw"></i> {{ _('PDF invoice') }}</a>
              <a class="btn btn-primary btn-margin-bottom" v-if="isPrintTransferVisible" v-on:click="printTransfer"><i class="fa fa-file-pdf fa-fw"></i> {{ _('PDF bank transfer') }}</a>
              <a class="btn btn-primary btn-margin-bottom" v-if="isRefund || isRefunded" v-on:click="printRefund"><i class="fa fa-file-pdf fa-fw"></i> {{ _('PDF refund receipt') }}</a>
              <a class="btn btn-default btn-margin-bottom" v-if="hasOrder" v-on:click="displayOrder"><i class="fa fa-file fa-fw"></i> {{ _('Display order') }}</a>
              <a class="btn btn-default btn-margin-bottom" v-if="isDisplayRefundInvoiceVisible" v-on:click="displayRefundInvoice"><i class="fa fa-file fa-fw"></i> {{ _('Display refund transaction') }}</a>
              <a class="btn btn-default btn-margin-bottom" v-if="isDisplayRefundedInvoiceVisible" v-on:click="displayRefundedInvoice"><i class="fa fa-file fa-fw"></i> {{ _('Display refunded transaction') }}</a>
              <a class="btn btn-default btn-margin-bottom" v-if="isPdfInvoiceAvailable" v-on:click="displayPdf"><i class="fa fa-file-pdf fa-fw"></i> {{ _('PDF bill') }}</a>
              <a class="btn btn-default btn-margin-bottom" v-if="isDisplayConsolidatedVisible" v-on:click="displayConsolidatedOrder"><i class="fa fa-file fa-fw"></i> {{ _('Display consolidated order') }}</a>
              <a class="btn btn-default btn-margin-bottom" v-if="isResendToNetvisorVisible" v-on:click="resendToNetvisor"><i class="fa fa-retweet fa-fw"></i> {{ _('Resend to Netvisor') }}</a>
              <a class="btn btn-default btn-margin-bottom" v-if="isResendToBrioxVisible" v-on:click="resendToBriox"><i class="fa fa-retweet fa-fw"></i> {{ _('Resend to Briox') }}</a>
              <a class="btn btn-default btn-margin-bottom" v-if="isResendToNocfoVisible" v-on:click="resendToNocfo"><i class="fa fa-retweet fa-fw"></i> {{ _('Resend to NoCFO') }}</a>
              <a class="btn btn-warning btn-margin-bottom" v-if="isToggleRefundVisible" v-on:click="toggleRefund"><i class="fa fa-money-bill-transfer fa-fw"></i> <template v-if="isRefund">{{ _('Cancel refund status') }}</template><template v-else>{{ _('Mark as refund') }}</template></a>
            </div>
            <div class="row" v-if="isNetvisorStatusVisible">
              <div class="col-sm-6">
                <h3>{{ _('Netvisor') }}</h3>
                <div class="alert alert-warning" v-if="isNetvisorErrorVisible">
                  <p class="accounting-error">{{ invoice.netvisor_error }}</p>
                </div>
                <div class="alert alert-success" v-else-if="parseInt(invoice.netvisor)">
                  <p>{{ _('Sent to Netvisor') }} {{ DateTime.fromSeconds(Number(invoice.netvisor_ts), { zone: timezone }).toFormat(dateFormat + " '" + _('at') + "' " + timeFormat) }}</p>
                </div>
              </div>
            </div>
            <div class="row" v-if="isBrioxStatusVisible">
              <div class="col-sm-6">
                <h3>{{ _('Briox') }}</h3>
                <div class="alert alert-warning" v-if="isBrioxErrorVisible">
                  <p class="accounting-error">{{ invoice.briox_error }}</p>
                </div>
                <div class="alert alert-success" v-else-if="parseInt(invoice.briox)">
                  <p>{{ _('Sent to Briox') }} {{ DateTime.fromSeconds(Number(invoice.briox_ts), { zone: timezone }).toFormat(dateFormat + " '" + _('at') + "' " + timeFormat) }}</p>
                </div>
              </div>
            </div>
            <div class="row" v-if="isNocfoStatusVisible">
              <div class="col-sm-6">
                <h3>{{ _('NoCFO') }}</h3>
                <div class="alert alert-warning" v-if="isNocfoErrorVisible">
                  <p class="accounting-error">{{ invoice.nocfo_error }}</p>
                </div>
                <div class="alert alert-success" v-else-if="parseInt(invoice.nocfo)">
                  <p>{{ _('Sent to NoCFO') }} {{ DateTime.fromSeconds(Number(invoice.nocfo_ts), { zone: timezone }).toFormat(dateFormat + " '" + _('at') + "' " + timeFormat) }}</p>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-sm-6">
                <div class="alert alert-success">
                  <div class="form-group">
                    <strong>{{ _('Payment status') }}</strong>
                    <p v-if="parseInt(invoice.paid)">{{ _('Transaction has been paid') }}</p>
                    <p v-else>{{ _('Transaction has not been paid') }}</p>
                  </div>
                  <div class="form-group" v-if="parseInt(invoice.paid)">
                    <strong>{{ _('Payment date') }}</strong>
                    <p>{{ $filters.timestampToDate(invoice.paymentdate) }}</p>
                  </div>
                  <button type="button" class="btn btn-primary" id="sendPaymentConfirmation" v-if="canSendPaymentConfirmation" v-on:click="sendPaymentConfirmation" :disabled="sendingPaymentConfirmation ? true : undefined"><i class="fa fa-fw fa-envelope"></i> {{ _('Send payment confirmation') }}</button>
                </div>
                <div class="form-group">
                  <table width="100%" class="table">
                    <thead>
                      <tr>
                        <th colspan="3">{{ _('Payments') }}</th>
                      </tr>
                    </thead>
                    <tbody id="paymentsContainer">
                      <template v-for="payment in payments">
                        <tr class="payment-row">
                          <td>{{ $filters.timestampToDate(payment.paymentdate) }}</td>
                          <td>
                            <span v-if="payment.valuecard">{{ payment.valuecard.name }}<span v-if="payment.valuecard.code">: {{ payment.valuecard.code }}</span><span v-if="payment.valuecard.external"> ({{ payment.valuecard.external }})</span></span>
                            <span v-else-if="payment.voucher">{{ payment.voucher.name }}</span>
                            <span v-else>{{ getPaymentTitle(payment.method) }}</span>
                          </td>
                          <td align="right">{{ $filters.currency(payment.amount, settings.currency) }}</td>
                        </tr>
                        <tr class="payment-row-extra" v-if="payment.bambora_id">
                          <td>&nbsp;</td>
                          <td><small>{{ _('Visma Pay order number') }} {{ payment.bambora_id }}</small></td>
                          <td>&nbsp;</td>
                        </tr>
                        <tr class="payment-row-extra" v-if="payment.viva_id">
                          <td>&nbsp;</td>
                          <td><small>{{ _('Viva order number') }} {{ payment.viva_id }}</small></td>
                          <td>&nbsp;</td>
                        </tr>
                        <tr class="payment-row-extra" v-if="payment.mobilepay_reference">
                          <td>&nbsp;</td>
                          <td><small>{{ _('MobilePay reference') }} {{ payment.mobilepay_reference }}</small></td>
                          <td>&nbsp;</td>
                        </tr>
                        <tr class="payment-row-extra" v-if="payment.netvisoraccount">
                          <td>&nbsp;</td>
                          <td><small>{{ _('Netvisor account') }} {{ payment.netvisoraccount }}</small></td>
                          <td>&nbsp;</td>
                        </tr>
                        <tr class="payment-row-extra" v-if="payment.brioxpaymentmethod">
                          <td>&nbsp;</td>
                          <td><small>{{ _('Briox payment method') }} {{ payment.brioxpaymentmethod }}</small></td>
                          <td>&nbsp;</td>
                        </tr>
                      </template>
                    </tbody>
                  </table>
                  <button v-if="!isRefund && !isRefunded" type="button" class="btn btn-info" id="addPayment" v-on:click="addPayment"><i class="fa fa-fw fa-plus"></i> {{ _('Add payment') }}</button>
                  <hr v-if="hasUsedValuecards" />
                  <table width="100%" class="table" v-if="hasUsedValuecards">
                    <thead>
                      <tr>
                        <th colspan="3">{{ _('Used value cards') }}</th>
                      </tr>
                    </thead>
                    <tbody id="valuecardsContainer">
                      <tr v-for="valuecard in valuecards">
                        <td>{{ $filters.timestampToDate(valuecard.paymentdate) }}</td>
                        <td colspan="2">
                          <span v-if="valuecard.valuecard">{{ valuecard.valuecard.name }}<span v-if="valuecard.valuecard.code">: {{ valuecard.valuecard.code }}</span><span v-if="valuecard.valuecard.external"> ({{ valuecard.valuecard.external }})</span></span>
                          <span v-else-if="valuecard.voucher">{{ valuecard.voucher.name }}</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('messages')" class="tab-pane active" id="messages" v-if="isTabActive('messages')" key="messages">
            <div class="row">
              <div class="col-md-12">
                <div id="orderMessages">
                  <h3>{{ _('Messages') }}</h3>
                  <div class="table-responsive">
                    <table class="table table-striped" id="messageTable">
                      <thead>
                        <tr>
                          <th>{{ _('Date') }}</th>
                          <th>{{ _('Message subject') }}</th>
                          <th>{{ _('Recipient') }}</th>
                          <th>{{ _('Message') }}</th>
                          <th>{{ _('Status') }}</th>
                        </tr>
                      </thead>
                      <tbody v-if="invoice.messages && invoice.messages.length">
                        <tr v-for="message in invoice.messages" v-on:click="displayMessage(message)">
                          <td class="is-clickable">{{ $filters.timestampToDate(message.creationdate) }} {{ $filters.timestampToTime(message.creationdate) }}</td>
                          <td class="is-clickable"><a><strong>{{ message.subject }}</strong></a></td>
                          <td class="is-clickable">{{ message.to }}</td>
                          <td class="is-clickable">{{ message.summary }}</td>
                          <td class="is-clickable"><span v-if="message.latestEvent">{{ _(message.latestEvent.type) }}</span><span v-else>{{ _('Sent') }}</span></td>
                        </tr>
                      </tbody>
                      <tbody v-else>
                        <tr>
                          <td colspan="4">{{ _('No messages found.') }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-if="parseInt(invoice.skip_notifications)" class="well">
                    {{ _('Notifications:') }} {{ _('Skipped') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition-group>
      </div>
    </div>
    <nav class="save-bar">
      <div class="flex-row space-between">
        <template v-if="isRefundVisible">
          <Dropdown buttonClass="btn-default btn-danger" direction="up" v-if="hasVismaPayPayments || hasVivaPayments || hasMobilepayPayments || hasPaytrailPayments">
            <template #trigger>
              <i class="fa fa-fw fa-undo"></i> {{ _('Refund transaction') }} &nbsp;<span class="caret"></span>
            </template>
            <li><a v-if="hasVismaPayPayments" href="javascript:;" v-on:click="refund(false)">{{ _('Refund transaction and Visma Pay payments') }} <i></i></a></li>
            <li><a v-if="hasVivaPayments" href="javascript:;" v-on:click="refund(false)">{{ _('Refund transaction and Viva payments') }} <i></i></a></li>
            <li><a v-if="hasMobilepayPayments" href="javascript:;" v-on:click="refund(false)">{{ _('Refund transaction and MobilePay payments') }} <i></i></a></li>
            <li><a v-if="hasPaytrailPayments" href="javascript:;" v-on:click="refund(false)">{{ _('Refund transaction and Paytrail payments') }} <i></i></a></li>
            <li><a href="javascript:;" v-on:click="refund(true)">{{ _('Refund transaction') }} <i></i></a></li>
          </Dropdown>
          <a class="btn btn-danger btn-margin-bottom" v-else v-on:click="refund(true)"><i class="fa fa-undo fa-fw"></i> <span v-if="invoice.payment === 'cash'">{{ _('Refund receipt') }}</span><span v-else>{{ _('Refund transaction') }}</span></a>
        </template>
        <div class="button-col">
          <a class="btn btn-success" id="saveButton" v-if="showSaveButton" v-on:click="save" :disabled="saving ? true : undefined"><i class="fa fa-fw fa-save"></i> <span>{{ _('Save') }}</span></a>
          <a class="btn btn-default" id="cancelButton" v-on:click="close"><i class="fa fa-fw fa-close"></i><span class="hidden-xs">{{ _('Close') }}</span></a>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>

import axios from 'axios'
import { DateTime } from 'luxon'
import paymentTypes from '~/lib/paymenttypes'
import components from '~/lib/components'
import { isAnonymized } from '~/lib/anonymization'
import { mapGetters } from 'vuex'
const OrderDetails = defineAsyncComponent(() => import('~/components/modals/ModalOrderDetails'))
const ResourceDetails = defineAsyncComponent(() => import('~/components/modals/ModalResourceDetails'))
const ReservationDetails = defineAsyncComponent(() => import('~/components/modals/ModalReservationDetails'))
const ValuecardDetails = defineAsyncComponent(() => import('~/components/modals/ValuecardDetails'))
const SentMessageDetails = defineAsyncComponent(() => import('~/components/modals/SentMessageDetails'))
const PaymentDetails = defineAsyncComponent(() => import('~/components/modals/PaymentDetails'))

export default {
  data () {
    return {
      invoice: {},
      order: null,
      deleted: false,
      messages: [],
      DateTime: DateTime,
      paymentTypes: [],
      currentNotificationsWithoutPayment: null,
      sendingPaymentConfirmation: false,
      refunding: false
    }
  },
  mixins: [components, paymentTypes],
  computed: {
    tabList () {
      return this.editorTabs([
        { id: 'general', label: this._('General') },
        { id: 'messages', label: this._('Messages'), badge: this.invoice.messages && this.invoice.messages.length ? this.invoice.messages.length : null }
      ])
    },
    ...mapGetters(['countries']),
    isAnonymizedRecord () {
      return isAnonymized(this.invoice) || isAnonymized(this.order)
    },
    anonymizedLockStatus () {
      return this._('This record has been anonymized and cannot be modified.')
    },
    isResendBarVisible () {
      return this.hasOrder && !this.isRefund && !this.isRefunded && (this.hasValueCards || this.hasTickets)
    },
    hasValueCards () {
      return !!this.allItems.find(i => i.ownValuecard && i.ownValuecard.length)
    },
    hasActiveValueCards () {
      return !!this.allItems.find(i => i.ownValuecard && i.ownValuecard.some(v => parseInt(v.status)))
    },
   hasTickets () { // Check that we have a) tickets and b) reservations to shows that have not already ended
      return !!this.allItems.find(i => i.show_id && i.item_id) && !!this.allItems.find(i => i.show_id && i.end_ts && parseInt(i.end_ts, 10) > DateTime.now({ zone: this.timezone }).toUnixInteger())
    },
    allItems() {
      return this.invoice && this.invoice.ownItem ? this.invoice.ownItem : []
    },
    topLevelItems() {
      return this.allItems.filter(i => !i.item_id || (i.item_id && i.optiontype && parseInt(i.optiontype) === this.LINKEDPRODUCT_OFFER))
    },
    productItems () {
      return this.invoice && this.invoice.ownItem ? this.invoice.ownItem.filter(i => !i.reservation_datestart) : []
    },
    reservationItems () {
      return this.invoice && this.invoice.ownItem ? this.invoice.ownItem.filter(i => !!i.reservation_datestart) : []
    },
    deliveryName () {
      if (!this.invoice || !parseInt(this.invoice.delivery)) {
        return ''
      }
      if (this.shopDeliveryMethods[this.shopId]) {
        const dm = this.shopDeliveryMethods[this.shopId].find(d => parseInt(d.id, 10) === parseInt(this.invoice.delivery, 10))
        if (dm) {
          return dm.name
        }
      }
      return ''
    },
    paymentName () {
      return this.invoice ? this.getPaymentTitle(this.invoice.payment) : ''
    },
    bamboraId () {
      return this.invoice ? this.invoice.bamboraid : null
    },
    vivaId () {
      return this.invoice ? this.invoice.vivaid : null
    },
    isDisplayRefundInvoiceVisible () {
      return this.invoice && this.invoice.refund_id
    },
    isDisplayRefundedInvoiceVisible () {
      return this.invoice && this.invoice.refunds_id
    },
    isPdfInvoiceAvailable () {
      return !this.isRopoConfigured && !this.isNetvisorEnabled && !this.isBrioxEnabled && !this.isNocfoEnabled && !this.invoice.paid
    },
    isRopoConfigured () {
      return this.settings && this.settings.pay_option7_trustid && this.settings.pay_option7_trustapikey
    },
    isResendToNetvisorVisible () {
      return this.isNetvisorEnabled && this.invoice && !parseInt(this.invoice.netvisor)
    },
    isNetvisorStatusVisible () {
      return this.isNetvisorEnabled && this.invoice && (parseInt(this.invoice.netvisor) || this.invoice.netvisor_error)
    },
    isNetvisorErrorVisible () {
      return this.isNetvisorEnabled && this.invoice && this.invoice.netvisor_error
    },
    isNetvisorEnabled () {
      return this.settings && parseInt(this.settings.pay_option13)
    },
    isResendToBrioxVisible () {
      return this.isBrioxEnabled && this.invoice && !parseInt(this.invoice.briox)
    },
    isBrioxStatusVisible () {
      return this.isBrioxEnabled && this.invoice && (parseInt(this.invoice.briox) || this.invoice.briox_error)
    },
    isBrioxErrorVisible () {
      return this.isBrioxEnabled && this.invoice && this.invoice.briox_error
    },
    isBrioxEnabled () {
      return this.settings && parseInt(this.settings.pay_option20)
    },
    isResendToNocfoVisible () {
      return this.isNocfoEnabled && this.invoice && !parseInt(this.invoice.nocfo)
    },
    isNocfoStatusVisible () {
      return this.isNocfoEnabled && this.invoice && (parseInt(this.invoice.nocfo) || this.invoice.nocfo_error)
    },
    isNocfoErrorVisible () {
      return this.isNocfoEnabled && this.invoice && this.invoice.nocfo_error
    },
    isNocfoEnabled () {
      return this.settings && parseInt(this.settings.pay_option21)
    },
    isPrintReceiptVisible () {
      return this.invoice && parseInt(this.invoice.paid) && this.invoice.payment !== 'adminnone' && !this.isRefund
    },
    isPrintInvoiceVisible () {
      if (!this.invoice || !this.invoice.identifier) {
        return false
      }
      if (this.isNocfoEnabled) {
        return !!this.invoice.nocfo_invoice_id
      }
      if (this.isNetvisorEnabled) {
        return !!parseInt(this.invoice.netvisor)
      }
      if (this.isBrioxEnabled) {
        return !!this.invoice.briox_invoice_id
      }
      return this.invoice.payment === 'bill'
    },
    isInvoiceReferenceVisible () {
      if (!this.isInvoiceOrTransfer) {
        return false
      }
      if (this.isNocfoEnabled || this.isNetvisorEnabled || this.isBrioxEnabled) {
        return !!this.invoice.accounting_reference
      }
      return true
    },
    isPrintTransferVisible () {
      return this.invoice && this.invoice.identifier && (this.invoice.payment === 'transfer' || this.invoice.payment === 'cash') && !parseInt(this.invoice.isrefund)
    },
    isRefund () {
      return this.invoice && parseInt(this.invoice.isrefund)
    },
    isRefunded () {
      return this.invoice && parseInt(this.invoice.refunded)
    },
    isRefundVisible () {
      if (this.isAnonymizedRecord) {
        return false
      }
      return this.invoice && this.invoice.payment !== 'adminnone' && this.invoice.payment !== 'open' && Number(this.invoice.confirmed) !== this.ORDER_CONSOLIDATED && Number(this.invoice.confirmed) !== this.ORDER_CONFIRMED && !this.isRefunded && !this.isRefund && !this.refunding
    },
    isToggleRefundVisible () {
      if (this.isAnonymizedRecord) {
        return false
      }
      return this.invoice && !this.invoice.refunds_id && !this.isRefunded && (this.isRefund || this.invoice.totalWithDelivery < 0)
    },
    isPaid () {
      return this.invoice && parseInt(this.invoice.paid)
    },
    isDisplayConsolidatedVisible () {
      return this.invoice && parseInt(this.invoice.consolidate_id)
    },
    isInvoiceOrTransfer () {
      return this.invoice && (this.invoice.payment === 'bill' || this.invoice.payment === 'transfer')
    },
    hasOrder () {
      return this.order && this.order.id
    },
    hasBillingCustomer () {
      return this.invoice && (this.invoice.billing_name || this.invoice.billing_company)
    },
    afterSalesSent: {
      get () {
        if (!parseInt(this.invoice.aftersales_sent)) {
          return '0'
        }
        return this.invoice.aftersales_sent + ''
      },
      set (newValue) {
        this.invoice.aftersales_sent = newValue
      }
    },
    pdfUrl () {
      if (!this.invoice) {
        return 'about:blank'
      }
      let type = 'bill'
      if (this.invoice.payment === 'transfer') {
        type = 'transfer'
      }
      if (this.isRefund) {
        type = 'refund'
      }
      return this.baseDomain + this.shopId + (this.invoice.version ? '/' + this.invoice.version : '' ) + '/' + type + 's/' + this.invoice.identifier + '.pdf'
    },
    canSendPaymentConfirmation () {
      return this.order && this.invoice && this.invoice.name && this.invoice.email && !this.isRefund && this.isPaid
    },
    hasUsedValuecards () {
      return !!this.valuecards.length
    },
    payments () {
      return this.invoice && this.invoice.ownPayment ? this.invoice.ownPayment.filter(p => !(p.method === 'voucher' && parseFloat(p.amount) === 0)) : []
    },
    valuecards () {
      return this.invoice && this.invoice.ownPayment ? this.invoice.ownPayment.filter(p => p.method === 'voucher' && parseFloat(p.amount) === 0) : []
    },
    missingAmount () {
      return Math.round((parseFloat(this.invoice.totalWithDelivery) - this.invoice.ownPayment.reduce((a, cv) => { return a + parseFloat(cv.amount) }, 0)) * 100) / 100
    },
    showSaveButton () {
      if (this.isAnonymizedRecord) {
        return false
      }
      if (this.currentNotificationsWithoutPayment != null) {
        return true
      }
      if (!this.invoice || !this.invoice.ownPayment || !this.invoice.ownPayment.length) {
        return false
      }
      if (this.invoice.ownPayment.filter(p => !p.id).length) {
        return true
      }
      return false
    },
    notificationsWithoutPayment: {
      get () {
        if (this.currentNotificationsWithoutPayment !== null) {
          return this.currentNotificationsWithoutPayment
        }
        if (this.invoice && this.invoice.confirmed === this.ORDER_BILLED_AND_CONFIRMED) {
          return true
        }
        return false
      },
      set (newValue) {
        this.currentNotificationsWithoutPayment = !!newValue
      }
    },
    hasVismaPayPayments () {
      return this.payments ? this.payments.filter(p => p.method === 'paybywaychannel' || p.method === 'bambora' || (p.method === 'admincard' && parseInt(p.bambora_id))).length > 0 : false
    },
    hasVivaPayments () {
      return this.payments ? this.payments.filter(p => p.method === 'viva').length > 0 : false
    },
    hasMobilepayPayments () {
      return this.payments ? this.payments.filter(p => p.method === 'mobilepay' && p.mobilepay_reference).length > 0 : false
    },
    hasPaytrailPayments () {
      // Same conditions as the backend's Paytrail refund: checkout payment with a stored transaction id (-1 marks a failed payment)
      const transactionId = this.invoice && this.invoice.transactionid
      return !!this.invoice && this.invoice.payment === 'checkout' && !!transactionId && String(transactionId) !== '-1'
    },
    verifiedCustomer () {
      if (this.invoice && this.invoice.verifiedcustomer) {
        return { ts: this.invoice.verifiedcustomer.ts, ...JSON.parse(this.invoice.verifiedcustomer.data) }
      }
      return null
    }
  },
  methods: {
    getItemComponent(item) {
      return !!item.reservation_datestart ? 'ReservationItem' : 'ProductItem'
    },
    getItemOptions(item) {
      let items = this.allItems.filter(i => i.item_id === item.id)
      for (let i = 0; i < items.length; i++) {
        let children = this.getItemOptions(items[i])
        if (children && children.length) {
          items.splice(i + 1, 0, ...children)
        }
      }
      return items
    },
    getCountryName(id) {
      return this.countries[id] ? this.countries[id] : id
    },
    async toggleRefund () {
      let response
      try {
        response = await axios.patch(this.baseDomain + this.shopId + '/bills/' + this.invoice.id + '.json?apikey=' + this.apiKey, {
          id: this.invoice.id,
          action: 'toggle-refund'
        })
      } catch(e) {
        return
      }
    },
    close () {
      if (this.isModal) {
        this.$emit('close')
      } else {
        this.EventBus.$emit('hideDetailsColumn', {
          shopId: this.shopId,
          data: {
            trail: this.componentTrail
          }
        })
      }
    },
    async resendToNetvisor () {
      let response
      let data = {
        id: this.invoice.id,
        action: 'resend-netvisor'
      }
      try {
        response = await axios.patch(this.baseDomain + this.shopId + '/bills/' + this.invoice.id + '.json?apikey=' + this.apiKey, data)
      } catch(e) {
        return
      }
      this.showNotification(this._('Invoice queued for sending to Netvisor.'))
    },
    async resendToBriox () {
      let response
      let data = {
        id: this.invoice.id,
        action: 'resend-briox'
      }
      try {
        response = await axios.patch(this.baseDomain + this.shopId + '/bills/' + this.invoice.id + '.json?apikey=' + this.apiKey, data)
      } catch(e) {
        return
      }
      this.showNotification(this._('Invoice queued for sending to Briox.'))
    },
    async resendToNocfo () {
      let response
      let data = {
        id: this.invoice.id,
        action: 'resend-nocfo'
      }
      try {
        response = await axios.patch(this.baseDomain + this.shopId + '/bills/' + this.invoice.id + '.json?apikey=' + this.apiKey, data)
      } catch(e) {
        return
      }
      this.showNotification(this._('Invoice queued for sending to NoCFO.'))
    },
    async displayMessage (message) {
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/orders/' + message.order_id + '.json?message=' + message.identifier + '&apikey=' + this.apiKey)
      } catch(e) {
        return
      }
      const msg = response.data
      this.showModal('ordermessagedetails', SentMessageDetails, {
        props: {
          baseDomain: this.baseDomain,
          shopId: this.shopId,
          apiKey: this.apiKey,
          originalMessage: msg
        },
        events: {},
        handlers: {},
        width: 800
      })
    },
    displayResource (resource) {
      this.showModal('orderresourcedetails', ResourceDetails, {
        props: {
          baseDomain: this.baseDomain,
          shopId: this.shopId,
          apiKey: this.apiKey,
          component: {
            initData: {
              resourceId: resource.id,
              modal: true
            }
          }
        },
        events: {},
        handlers: {},
        width: 1000
      })
    },
    displayReservation (reservation) {
      this.showModal('orderreservationdetails', ReservationDetails, {
        props: {
          baseDomain: this.baseDomain,
          shopId: this.shopId,
          apiKey: this.apiKey,
          component: {
            initData: {
              reservationId: reservation.id,
              modal: true
            }
          }
        },
        events: {},
        handlers: {},
        width: 1000
      })
    },
    displayValuecard (valuecard) {
      this.showModal('valuecarddetails', ValuecardDetails, {
        props: {
          baseDomain: this.baseDomain,
          shopId: this.shopId,
          apiKey: this.apiKey,
          locale: this.locale,
          valuecard: valuecard,
          order: this.order,
          settings: this.settings
        },
        handlers: {},
        width: 800
      })
    },
    displayCustomer () {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'CustomerDetails',
        title: this._('Customer') + ' ' + this.invoice.customer_id + (this.invoice.name ? ': ' + this.invoice.name : ''),
        width: 6,
        data: {
          customerId: this.invoice.customer_id,
          trail: this.componentTrail,
          activateTab: 'general'
        }
      })
    },
    displayBillingCustomer () {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'BillingCustomerDetails',
        title: this._('Billing Customer') + ' ' + this.invoice.billingcustomer_id + (this.invoice.billing_company ? ': ' + this.invoice.billing_company : ''),
        width: 6,
        data: {
          billingCustomerId: this.order.billingcustomer_id,
          trail: this.componentTrail,
          activateTab: 'general'
        }
      })
    },
    displayOrder () {
      if (this.isModal) {
        this.showModal('invoiceorderdetails', OrderDetails, {
          props: {
            baseDomain: this.baseDomain,
            shopId: this.shopId,
            apiKey: this.apiKey,
            component: {
              initData: {
                orderId: this.order.id,
                modal: true
              }
            }
          },
          events: {},
          handlers: {},
          width: 1000
        })
      } else {
        this.EventBus.$emit('showDetailsColumn', {
          shopId: this.shopId,
          component: 'OrderDetails',
          title: this._('Order') + ' ' + this.order.id + (this.order.name ? ': ' + this.order.name : ''),
          width: 6,
          data: {
            orderId: this.order.id,
            trail: this.componentTrail
          }
        })
      }
    },
    displayRefundInvoice () {
      if (this.isModal) {
        this.showModal('refundinvoicedetails', InvoiceDetails, {
          props: {
            baseDomain: this.baseDomain,
            shopId: this.shopId,
            apiKey: this.apiKey,
            component: {
              initData: {
                invoiceId: this.invoice.refund_id,
                modal: true
              }
            }
          },
          events: {},
          handlers: {},
          width: 1000
        })
      } else {
        this.EventBus.$emit('showDetailsColumn', {
          shopId: this.shopId,
          component: 'InvoiceDetails',
          title: this._('Refund') + ' ' + this.invoice.refund_id,
          width: 6,
          data: {
            invoiceId: this.invoice.refund_id,
            trail: this.componentTrail
          }
        })
      }
    },
    displayRefundedInvoice () {
      if (this.isModal) {
        this.showModal('refundedinvoicedetails', InvoiceDetails, {
          props: {
            baseDomain: this.baseDomain,
            shopId: this.shopId,
            apiKey: this.apiKey,
            component: {
              initData: {
                invoiceId: this.invoice.refunds_id,
                modal: true
              }
            }
          },
          events: {},
          handlers: {},
          width: 1000
        })
      } else {
        this.EventBus.$emit('showDetailsColumn', {
          shopId: this.shopId,
          component: 'InvoiceDetails',
          title: this._('Transaction') + ' ' + this.invoice.refunds_id,
          width: 6,
          data: {
            invoiceId: this.invoice.refunds_id,
            trail: this.componentTrail
          }
        })
      }
    },
    displayConsolidatedOrder () {
      this.displayOrder(this.order.consolidate_id)
    },
    displayPdf () {
      window.open(this.pdfUrl)
    },
    printInvoice () {
      window.open(this.baseDomain + this.shopId + (this.invoice.version ? '/' + this.invoice.version : '' ) + '/bills/' + this.invoice.identifier + '.pdf')
    },
    printReceipt () {
      window.open(this.baseDomain + this.shopId + (this.invoice.version ? '/' + this.invoice.version : '' ) + '/receipts/' + this.invoice.identifier + '.pdf')
    },
    printTransfer () {
      window.open(this.baseDomain + this.shopId + (this.invoice.version ? '/' + this.invoice.version : '' ) + '/transfers/' + this.invoice.identifier + '.pdf')
    },
    printRefund () {
      if (this.isRefund) {
        window.open(this.baseDomain + this.shopId + (this.invoice.version ? '/' + this.invoice.version : '' ) + '/refunds/' + this.invoice.identifier + '.pdf')
      } else {
        window.open(this.baseDomain + this.shopId + (this.invoice.version ? '/' + this.invoice.version : '' ) + '/refunds/' + this.invoice.refundidentifier + '.pdf')
      }
    },
    refund (skipBambora) {
      this.showDialog({
        title: this._('Are you sure you want to refund the invoice? Refunding can\'t be undone.'),
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Ok'),
            class: 'success',
            icon: 'check',
            handler: () => {
              this.refundInvoice(skipBambora)
            }
          }, 
        ]
      })
    },
    refundInvoice (skipBambora) {
      return new Promise(async (resolve, reject) => {
        let response
        this.refunding = true
        try {
          const data = {
            identifier: this.invoice.identifier
          }
          response = await axios.post(this.baseDomain + this.shopId + '/refunds/new.json?' + (skipBambora ? 'skipbambora=true&' : 'localmerchant=true&') + 'apikey=' + this.apiKey, data)
          this.showNotification(this._('Refund completed!'))
          await this.loadInvoice(this.invoice.id)
          resolve(response)
        } catch(e) {
          this.showError(this._('Refund failed!'))
          reject(e)
        } finally {
          this.refunding = false
        }
      })
    },
    activateValueCardsDialog () {
      this.showDialog({
        title: this._('Activate value cards'),
        width: 800,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Activate'),
            class: 'success',
            icon: 'check',
            handler: this.activateValueCards
          }
        ],
      })
    },
    activateValueCards () {
      return new Promise(async (resolve, reject) => {
        this.showNotification(this._('Processing, please wait...'))
        let response
        const order = this.order
        try {
          const data = {
            id: order.id,
          }
          let versionStr = this.versionStr
          if (order.version) {
            versionStr = '&version=' + order.version
          }
          response = await axios.post(this.baseDomain + this.shopId + (this.versionStr ? '/' + this.version : '') + '/orders/' + order.id + '.json?activatevaluecards=true&apikey=' + this.apiKey + versionStr, data)
        } catch(e) {
          this.showError(e)
          return reject()
        }
        this.loadInvoice(this.invoice.id)
        resolve()
      })
    },
    resendValueCardEmailDialog () {
      this.showDialog({
        title: this._('Resend value card email'),
        width: 800,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Send email'),
            class: 'success',
            icon: 'send',
            requiredFields: ['email'],
            handler: this.resendValueCardEmail
          }
        ],
        fields: [
          {
            title: this._('Email'),
            type: 'email',
            name: 'email',
            defaultValue: this.order.email ? this.order.email : ''
          }
        ]
      })
    },
    resendValueCardEmail (params) {
      return new Promise(async (resolve, reject) => {
        this.showNotification(this._('Processing, please wait...'))
        let response
        const order = this.order
        try {
          const data = {
            id: order.id,
            overrideEmail: params.email
          }
          let versionStr = this.versionStr
          if (order.version) {
            versionStr = '&version=' + order.version
          }
          response = await axios.post(this.baseDomain + this.shopId + (this.versionStr ? '/' + this.version : '') + '/orders/' + order.id + '.json?resendvaluecards=true&apikey=' + this.apiKey + versionStr, data)
        } catch(e) {
          this.showError(e)
          return reject()
        }
        this.showNotification(this._('Message sent!'))
        resolve()
      })
    },
    resendTicketEmailDialog () {
      this.showDialog({
        title: this._('Resend ticket email'),
        width: 800,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Send email'),
            class: 'success',
            icon: 'send',
            requiredFields: ['email'],
            handler: this.resendTicketEmail
          }
        ],
        fields: [
          {
            title: this._('Email'),
            type: 'email',
            name: 'email',
            defaultValue: this.order.email ? this.order.email : ''
          }
        ]
      })
    },
    resendTicketEmail (params) {
      return new Promise(async (resolve, reject) => {
        this.showNotification(this._('Processing, please wait...'))
        let response
        const order = this.order
        try {
          const data = {
            id: order.id,
            overrideEmail: params.email
          }
          let versionStr = this.versionStr
          if (order.version) {
            versionStr = '&version=' + order.version
          }
          response = await axios.post(this.baseDomain + this.shopId + (this.versionStr ? '/' + this.version : '') + '/orders/' + order.id + '.json?resendtickets=true&apikey=' + this.apiKey + versionStr, data)
        } catch(e) {
          this.showError(e)
          return reject()
        }
        this.showNotification(this._('Message sent!'))
        resolve()
      })
    },    
    loadInvoice (id) {
      return new Promise(async (resolve, reject) => {
        this.loading = true
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/bills/' + id + '.json?items=true&reservations=true&bamboraid=true&details=true&messages=true&verifiedcustomer=true&apikey=' + this.apiKey)
        } catch (e) {
          this.loading = false
          this.showError(e)
          reject()
          return
        }
        this.invoice = response.data
        this.processed = this.invoice.processed
        try {
          await this.loadOrder(this.invoice.identifier)
        } catch(e) {
        }
        this.loadPaymentTypes()
        this.storeState()
        this.loading = false
        resolve(this.invoice)
      })
    },
    loadOrder (identifier) {
      return new Promise(async (resolve, reject) => {
        this.order = null
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/orders/' + identifier + '.json?apikey=' + this.apiKey)
        } catch (e) {
          reject()
          return
        }
        this.order = response.data
        resolve(this.order)
      })
    },
    loadPaymentTypes () {
      return new Promise(async (resolve, reject) => {
        let response
        this.paymentTypes = []
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/bills.json?paymenttypes=true&apikey=' + this.apiKey)
        } catch (e) {
          reject()
          return
        }
        this.paymentTypes = response.data
        resolve(this.paymentTypes)
      })
    },
    sendPaymentConfirmation () {
      this.sendingPaymentConfirmation = true
      this.sendConfirmation('paymentconfirmation', () => {
        this.sendingPaymentConfirmation = false
      })
    },
    async sendConfirmation(type, callback) {
      let response
      let versionStr = this.versionStr
      if (this.invoice && this.invoice.version) {
        versionStr = '&version=' + this.invoice.version
      }
      try {
        response = await axios.post(this.baseDomain + this.shopId + (this.versionStr ? '/' + this.version : '') + '/email/' + type + '/' + this.order.id + '.json?apikey=' + this.apiKey + versionStr)
        this.showNotification(this._('Payment confirmation was sent'))
        if (callback) {
          callback(true)
        }
      } catch(e) {
        this.showError(this._('Sending the message failed!'))
        if (callback) {
          callback(false)
        }
        return
      }
    },
    addPayment () {
      this.showModal('orderpaymentdetails', PaymentDetails, {
        props: {
          locale: this.locale,
          baseDomain: this.baseDomain,
          shopId: this.shopId,
          apiKey: this.apiKey,
          paymentTypes: this.paymentTypes,
          missingAmount: this.missingAmount
        },
        events: {
          addPayment: (payment) => {
            this.invoice.ownPayment.push(payment)
          }
        },
        handlers: {},
        width: 600
      })
    },
    getSaveData () {
      // Temporary fix for component state error 
      if (!this.order || !this.order.id) {
        return {
          newPayment: this.invoice.ownPayment.filter(p => !p.id).map(p => {
            return {
              paymentdate: p.paymentdate,
              type: p.method,
              amount: p.amount,
              voucher: p.voucher ? p.voucher.id : null,
              mobilepayReference: p.mobilepay_reference ? p.mobilepay_reference : null
            }
          }),
          notifications_without_payment: this.notificationsWithoutPayment ? 1 : 0
        }
      }
      return {
        id: this.order.id,
        newPayment: this.invoice.ownPayment.filter(p => !p.id).map(p => {
          return {
            paymentdate: p.paymentdate,
            type: p.method,
            amount: p.amount,
            voucher: p.voucher ? p.voucher.id : null,
            mobilepayReference: p.mobilepay_reference ? p.mobilepay_reference : null
          }
        }),
        notifications_without_payment: this.notificationsWithoutPayment ? 1 : 0
      }
    },
    async save () {
      if (this.isAnonymizedRecord) {
        return
      }
      if (!this.order || !this.order.id) {
        return this.saveRefundedInvoice()
      }
      this.saving = true
      const data = this.getSaveData()
      let response
      try {
        response = await axios.put(this.baseDomain + this.shopId + '/orders/' + this.order.id + '.json?apikey=' + this.apiKey, data)
        this.showNotification(this._('Changes saved successfully!'))
      } catch(e) {
        this.saving = false
        this.showError(e)
      }
      this.$emit('save', this.order)
      if (this.saveShouldClose) {
        this.close()
      }
    },
    async saveRefundedInvoice () {
      this.saving = true
      const data = {
        id: this.invoice.id,
        paid: this.invoice.paid,
        paymentdate: this.invoice.paymentdate,
        newPayment: this.invoice.ownPayment.filter(p => !p.id).map(p => p.paymentdate + ':' + p.method + ':' + p.amount + ':' + (p.voucher ? p.voucher.id : ''))
      }
      let response
      try {
        response = await axios.put(this.baseDomain + this.shopId + '/bills/' + this.invoice.id + '.json?apikey=' + this.apiKey, data)
      } catch(e) {
        this.saving = false
      }
      this.showNotification(this._('Changes saved successfully!'))
      this.$emit('save', this.invoice)
      if (this.saveShouldClose) {
        this.close()
      }
    }
  },
  components: {
    ProductItem: defineAsyncComponent(() => import('~/components/ProductItem')),
    ReservationItem: defineAsyncComponent(() => import('~/components/ReservationItem')),
  },
  async created () {
    if (this.component && this.component.initData) {
      try {
        await this.loadInvoice(this.component.initData.invoiceId)
      } catch(e) {}
    }
  },
  async updated () {
    if (this.component && this.component.initData) {
      if (!this.deleted && (this.invoice && this.invoice.id && parseInt(this.invoice.id, 10) !== parseInt(this.component.initData.invoiceId, 10))) {
        try {
          await this.loadInvoice(this.component.initData.invoiceId)
        } catch(e) {}
      }
    }
  },
  async mounted () {
    if(!this.events) {
      let events = {}
      events['billUpdate'] = async (message) => {
        if (!this.deleted && message && message.data && this.invoice && parseInt(message.data.id, 10) === parseInt(this.invoice.id, 10)) {
          if (!this.unsavedChanges) { // Only update if we haven't made any changes
            try {
              await this.loadInvoice(message.data.id)
            } catch(e) {}
          }
        }
      }
      this.events = events
    }
    this.enableEventBus()
  }
}

</script>

<style scoped>
.rightbuttons {
  float: right;
}
/* Accounting errors are raw API text and can carry newlines and XML tags */
.accounting-error {
  white-space: pre-wrap;
  word-break: break-word;
}
</style>